/**
 * S1-exec-from-imports: the Executive page's per-line source switch.
 *
 * public.exec_line_source (migration 0082) says, for every workbook line and
 * month, which source the page shows: OUR figure, computed by the pipeline
 * from the client's imports (audit/exec_line_source.py), or the client's own
 * workbook figure from public.exec_kpi. The pipeline makes every decision;
 * this module only joins the two tables and never decides a source itself.
 *
 * Rules it keeps:
 *   - One source per line. An "ours" line shows our value in every month and
 *     a blank (with the reason on hover) where we hold none; it is never
 *     back-filled from the workbook. A "workbook" line shows the workbook in
 *     every month.
 *   - One exception, decided by the pipeline and marked per month (AJ
 *     2026-09-30, client definitions): a line released to our figure keeps
 *     the workbook's figure in the months BEFORE our imports begin, where the
 *     pipeline writes that month's row as shown_source 'workbook'. Those
 *     months keep the workbook's own provenance, so the page says whose number
 *     each month is; the page never shows a blank where the workbook has one.
 *     The line's source is the source of its latest month.
 *   - Every line carries its source label, "From your imports" or "From your
 *     workbook", so the reader always knows whose number it is.
 *   - The workbook figure behind an "ours" line is kept on the row
 *     (workbook_value) so STAFF can see it as the check. Client sessions are
 *     never shown it on an "ours" line.
 *   - A line the table does not cover (the table is not there yet, or the
 *     register has not run since the workbook changed) keeps the workbook,
 *     labelled, which is exactly what the page showed before this switch.
 *
 * Plain .mjs so node:test covers it without React (the execKpi.mjs precedent).
 */

export const EXEC_LINE_SOURCE_TABLE = "exec_line_source";

/** The columns the page reads. Every name is a column of public.exec_line_source. */
export const EXEC_LINE_SOURCE_COLUMNS = [
  "sheet",
  "client_label",
  "period",
  "shown_source",
  "our_value",
  "our_unit",
  "metric_id",
  "reason_code",
  "reason_detail",
  "line_latest_period",
].join(",");

export const SOURCE_OURS = "ours";
export const SOURCE_WORKBOOK = "workbook";

/** The label every line carries. */
export const SOURCE_LABELS = Object.freeze({
  ours: "From your imports",
  workbook: "From your workbook",
});

/** The provenance an "ours" row takes, so the chip and CSV say so. */
export const IMPORTS_PROVENANCE = "meridian_imports";

/**
 * Why a line shows the workbook (or why an "ours" month is blank), in plain
 * words for the staff check and the register. Keys mirror the migration's
 * reason_code CHECK and audit/exec_line_source.REASON_CODES.
 */
export const REASON_COPY = Object.freeze({
  unmapped: "Not mapped to a Meridian metric yet",
  not_listed: "Not in the line map (a new or renamed workbook line)",
  not_comparable: "Measures something different from Meridian's figure",
  one_side: "One side of a metric; no single imported figure carries it",
  feed_missing: "A source feed is not imported yet",
  needs_calendar: "Per-day figure; needs the operating calendar",
  not_defined: "Not defined at this sheet's level",
  unit_mismatch: "Units differ from Meridian's figure",
  pinned: "Pinned to the workbook by Meridian staff",
  no_value: "Meridian holds no figure for this month",
  out_of_band: "Computed with your definition; does not reconcile with your workbook yet",
  no_band: "Computed with your definition; no agreed tolerance to check it against yet",
  input_held: "Built from a line that does not reconcile yet",
  no_check: "Computed with your definition; your workbook has no month to check it against",
  not_built: "Line source not built yet",
});

function toNumber(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function key(sheet, label) {
  return `${sheet}\u0000${label}`;
}

/** Plain words for a reason code, or null. */
export function reasonText(code, detail) {
  if (!code) return null;
  const base = REASON_COPY[code] ?? code;
  return detail ? `${base}: ${detail}` : base;
}

/**
 * Index exec_line_source rows by line. Each entry carries the line's shown
 * source (the source of its latest month: the pipeline writes one source per
 * line, except that a released line's months before our imports begin keep
 * the workbook and say so month by month) and the per-month value, source and
 * reason.
 */
export function indexLineSources(rows) {
  const index = new Map();
  for (const row of rows ?? []) {
    if (!row || typeof row.sheet !== "string" || typeof row.client_label !== "string") continue;
    if (typeof row.period !== "string") continue;
    const k = key(row.sheet, row.client_label);
    let entry = index.get(k);
    const shown = row.shown_source === SOURCE_OURS ? SOURCE_OURS : SOURCE_WORKBOOK;
    if (!entry) {
      entry = {
        source: SOURCE_WORKBOOK,
        reasonCode: null,
        reasonDetail: null,
        latest: typeof row.line_latest_period === "string" ? row.line_latest_period : null,
        metricId: typeof row.metric_id === "string" ? row.metric_id : null,
        byPeriod: {},
      };
      index.set(k, entry);
    }
    entry.byPeriod[row.period] = {
      source: shown,
      value: toNumber(row.our_value),
      reasonCode: typeof row.reason_code === "string" ? row.reason_code : null,
      reasonDetail: typeof row.reason_detail === "string" ? row.reason_detail : null,
    };
  }
  // The line's source and reason are the ones its latest month carries. A
  // line whose latest month is the workbook's is the workbook's in every
  // month (the safe side); only an "ours" line keeps per-month workbook months.
  for (const entry of index.values()) {
    const periods = Object.keys(entry.byPeriod).sort();
    const at =
      entry.latest && entry.byPeriod[entry.latest] ? entry.latest : periods[periods.length - 1];
    const head = at ? entry.byPeriod[at] : null;
    entry.source = head && head.source === SOURCE_OURS ? SOURCE_OURS : SOURCE_WORKBOOK;
    if (entry.source === SOURCE_WORKBOOK && head) {
      entry.reasonCode = head.reasonCode;
      entry.reasonDetail = head.reasonDetail;
    }
  }
  return index;
}

/**
 * The exec_kpi rows with each line's shown source applied. Returns NEW rows
 * (the inputs are not mutated). Every row gains:
 *   source             "ours" | "workbook"
 *   source_reason      the line-level reason text when the line shows the
 *                      workbook, else null
 *   workbook_value     the workbook's own value for the month (the staff check)
 *   workbook_cell_text the workbook's own error text for the month
 *   missing_reason     on an "ours" row with no value, why
 * and an "ours" row's value, cell_text and provenance become ours.
 */
export function applyLineSources(execRows, index) {
  const idx = index instanceof Map ? index : indexLineSources(index);
  const out = [];
  for (const row of execRows ?? []) {
    if (!row || typeof row.sheet !== "string" || typeof row.line_item !== "string") continue;
    const entry = idx.get(key(row.sheet, row.line_item)) ?? null;
    const base = {
      ...row,
      workbook_value: row.value ?? null,
      workbook_cell_text: row.cell_text ?? null,
      missing_reason: null,
    };
    if (!entry || entry.source !== SOURCE_OURS) {
      out.push({
        ...base,
        source: SOURCE_WORKBOOK,
        source_reason: entry
          ? reasonText(entry.reasonCode, entry.reasonDetail)
          : REASON_COPY.not_built,
      });
      continue;
    }
    const cell = entry.byPeriod[row.period] ?? null;
    if (cell && cell.source === SOURCE_WORKBOOK) {
      // A month before our imports begin, on a line released to our figure:
      // the workbook's own figure, with the workbook's own provenance, so the
      // page says whose number it is. Never blank where the workbook has one.
      out.push({
        ...base,
        source: SOURCE_OURS,
        source_reason: null,
        month_source: SOURCE_WORKBOOK,
        missing_reason: null,
      });
      continue;
    }
    const value = cell ? cell.value : null;
    out.push({
      ...base,
      source: SOURCE_OURS,
      source_reason: null,
      value,
      cell_text: null,
      provenance: IMPORTS_PROVENANCE,
      missing_reason:
        value === null
          ? cell
            ? reasonText(cell.reasonCode, cell.reasonDetail)
            : REASON_COPY.no_value
          : null,
    });
  }
  return out;
}

/**
 * How many lines of a grouped sheet show each source. Used by the header and
 * the staff summary; counts lines, not cells.
 */
export function sourceCounts(items) {
  const counts = { ours: 0, workbook: 0 };
  for (const it of items ?? []) {
    counts[it && it.source === SOURCE_OURS ? "ours" : "workbook"] += 1;
  }
  return counts;
}

/**
 * The staff check for one month of an "ours" line: the workbook's figure and
 * the relative gap of ours from it. Null when either side is missing. Never
 * shown to a client session.
 */
export function staffCheck(ours, workbook) {
  const a = toNumber(ours);
  const b = toNumber(workbook);
  if (a === null || b === null) return null;
  return { workbook: b, gap: a - b, gapPct: b === 0 ? null : (a - b) / Math.abs(b) };
}

/**
 * The staff register's "Page shows" cell for one line and month: which source
 * the Executive page shows, our value where it shows ours (a ratio included,
 * sum over sum as the pipeline computed it), and why ours is missing where it
 * is. `entry` is one value of indexLineSources; null when the line has no
 * source row (the switch has not run since the workbook changed).
 */
export function lineSourceCell(entry, period) {
  if (!entry) {
    return { source: SOURCE_WORKBOOK, value: null, reason: REASON_COPY.not_built };
  }
  if (entry.source !== SOURCE_OURS) {
    return {
      source: SOURCE_WORKBOOK,
      value: null,
      reason: reasonText(entry.reasonCode, entry.reasonDetail),
    };
  }
  const cell = entry.byPeriod[period] ?? null;
  if (cell && cell.source === SOURCE_WORKBOOK) {
    // A month before our imports begin on a released line: the workbook's.
    return {
      source: SOURCE_WORKBOOK,
      value: null,
      reason: reasonText(cell.reasonCode, cell.reasonDetail),
    };
  }
  const value = cell ? cell.value : null;
  return {
    source: SOURCE_OURS,
    value,
    reason:
      value === null
        ? cell
          ? reasonText(cell.reasonCode, cell.reasonDetail)
          : REASON_COPY.no_value
        : null,
  };
}

/** The index key for a sheet and line, for callers holding an index. */
export function lineKey(sheet, label) {
  return key(sheet, label);
}

/**
 * Lines by the source the page shows, and workbook lines by reason code, with
 * the feeds named where a feed is missing. Counts LINES, not cells.
 */
export function summarizeLineSources(index) {
  const out = { lines: 0, ours: 0, workbook: 0, reasons: {}, feeds: {} };
  for (const entry of index?.values?.() ?? []) {
    out.lines += 1;
    if (entry.source === SOURCE_OURS) {
      out.ours += 1;
      continue;
    }
    out.workbook += 1;
    const code = entry.reasonCode ?? "not_built";
    out.reasons[code] = (out.reasons[code] ?? 0) + 1;
    if (code === "feed_missing" && entry.reasonDetail) {
      const feeds = entry.reasonDetail.replace(/^waiting on /, "");
      out.feeds[feeds] = (out.feeds[feeds] ?? 0) + 1;
    }
  }
  return out;
}
